import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { PageHead } from "@/components/Brand";
import { KETE } from "@/lib/content";
import { errMsg, startPlanPath, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export const ChoiceCard = ({ selected, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
    className={`w-full text-left rounded-2xl border px-5 py-4 flex items-center justify-between gap-4 min-h-[56px] ${selected ? "bg-charcoal border-charcoal text-alabaster" : "bg-white border-rule text-ink hover:border-charcoal/40"}`}
    style={{ transition: "background-color .35s ease, border-color .35s ease, color .35s ease" }}>
    <span className="text-[15px] sm:text-base">{children}</span>
    <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${selected ? "bg-gold" : "border border-rule"}`}>
      {selected && <Check size={15} className="text-charcoal" strokeWidth={2.5} />}
    </span>
  </button>
);

export default function Baskets() {
  const { me, refresh } = useMe();
  const api = useApi();
  const nav = useNavigate();
  const [sel, setSel] = useState(me?.baskets || {});
  const [busy, setBusy] = useState(false);
  const firstTime = !me?.baskets;
  const ready = KETE.every((k) => sel[k.key]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/baskets", sel);
      const fresh = await refresh();
      if (firstTime) nav(startPlanPath(fresh));
      else { toast.success("Your three baskets are updated"); nav("/"); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell nav={!firstTime}>
      <PageHead eyebrow="My Three Baskets · Ngā Kete o te Wānanga" title="Where would you like to start?" sub="Choose 3 focus areas to begin with, one from each kete." />
      <div className="space-y-10" data-testid="baskets-page">
        {KETE.map((k) => (
          <section key={k.key} className="fade-in">
            <h2 className="text-xl sm:text-2xl">{k.name}</h2>
            <p className="text-ink-soft mb-4">{k.desc}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {k.areas.map((a) => (
                <ChoiceCard key={a} selected={sel[k.key] === a} onClick={() => setSel({ ...sel, [k.key]: a })} testId={`basket-${k.key}-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{a}</ChoiceCard>
              ))}
            </div>
          </section>
        ))}
      </div>
      {(firstTime || JSON.stringify(sel) !== JSON.stringify(me.baskets)) && (
      <button className="btn-charcoal mt-10 w-full sm:w-auto sm:min-w-[220px] fade-in" disabled={!ready || busy} onClick={save} data-testid="baskets-save-btn">
        {busy ? "Saving" : firstTime ? "Continue" : "Save my baskets"}
      </button>
      )}
    </AppShell>
  );
}
